<template>
  <a-modal
    :visible="visible"
    title="Descargar stock de empleados"
    @update:visible="(value) => emit('update:visible', value)"
    @cancel="close"
  >
    <slot name="company" />

    <a-button
      v-if="filtersEnabled"
      :type="filtersVisible ? 'primary' : 'default'"
      class="mt-3"
      @click="toggleFilters"
    >
      {{ filtersVisible ? 'Ocultar filtros' : 'Agregar filtros (opcional)' }}
    </a-button>

    <div v-if="filtersEnabled && filtersVisible" class="mt-3">
      <a-row align="middle" justify="space-between" class="mb-2">
        <a-col>
          <a-typography-text class="fw-semibold fs-6">
            Pólizas
          </a-typography-text>
        </a-col>
        <a-col>
          <a-checkbox-group v-model:value="statuses" :disabled="loading">
            <a-checkbox :value="POLICY_STATUS.ACTIVE">
              Vigentes
              <a-tooltip
                title="Trae todo lo de las pólizas vigentes hoy, incluidas las personas que ya fueron dadas de baja."
              >
                <info-circle-outlined class="info-icon" @click.prevent />
              </a-tooltip>
            </a-checkbox>
            <a-checkbox :value="POLICY_STATUS.INACTIVE">
              No vigentes
              <a-tooltip
                title="Trae todo lo de las pólizas que no están vigentes hoy, porque ya terminaron o aún no comienzan."
              >
                <info-circle-outlined class="info-icon" @click.prevent />
              </a-tooltip>
            </a-checkbox>
          </a-checkbox-group>
        </a-col>
      </a-row>
      <a-select
        v-model:value="selectedPolicyIds"
        mode="multiple"
        :options="policyOptions"
        :disabled="loading"
        :loading="loading"
        :placeholder="
          loading
            ? LOADING_POLICIES_REASON
            : statuses.length
            ? 'Seleccionar pólizas'
            : 'Marca Vigentes o No vigentes para ver las pólizas'
        "
        style="width: 100%"
      />
      <a-typography-text type="secondary" class="d-block mb-3">
        Vacío = todas las pólizas de los tipos marcados
      </a-typography-text>

      <a-typography-text class="d-block">
        Vigente al
        <a-tooltip
          title="Trae solo a las personas que estaban cubiertas en esa fecha."
        >
          <info-circle-outlined class="info-icon" />
        </a-tooltip>
      </a-typography-text>
      <a-date-picker
        v-model:value="activeDate"
        format="DD-MM-YYYY"
        placeholder="Selecciona una fecha"
        allow-clear
        style="width: 100%"
      />
      <a-typography-text type="secondary" class="d-block">
        Vacío = sin filtro de fecha
      </a-typography-text>
    </div>

    <template #footer>
      <a-button @click="close">Cancelar</a-button>
      <a-tooltip :title="okDisabled ? okDisabledText : ''">
        <span class="ok-button-wrapper">
          <a-button type="primary" :disabled="okDisabled" @click="submit">
            Aceptar
          </a-button>
        </span>
      </a-tooltip>
    </template>
  </a-modal>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import moment from 'moment';
import { InfoCircleOutlined } from '@ant-design/icons-vue';
import {
  POLICY_STATUS,
  buildStockDownloadParams,
  getDownloadState,
  policiesOfStatuses,
  pruneSelectedPolicies
} from '@/utils/stockDownloadFilters';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  // Company policies: [{ id, policyNumber, status }]
  policies: {
    type: Array,
    default: () => []
  },
  // Whether the filters can be shown (e.g. HR needs a selected company).
  filtersEnabled: {
    type: Boolean,
    default: true
  },
  // Extra reason to block the download (e.g. no company selected).
  blockedReason: {
    type: String,
    default: ''
  },
  // Filters reset when this value changes (e.g. the selected company).
  resetKey: {
    type: [String, Number],
    default: null
  },
  // While the policies load, the filters that depend on them are blocked.
  loading: {
    type: Boolean,
    default: false
  }
});

const LOADING_POLICIES_REASON = 'Cargando pólizas...';

const emit = defineEmits(['update:visible', 'download']);

const filtersVisible = ref(false);
const statuses = ref([]);
const selectedPolicyIds = ref([]);
const activeDate = ref(null);

const resetFilters = () => {
  statuses.value = [];
  selectedPolicyIds.value = [];
  activeDate.value = null;
};

const reset = () => {
  filtersVisible.value = false;
  resetFilters();
};

// The modal always opens clean, and hiding the filters discards them.
watch(
  () => props.visible,
  (visible) => {
    if (visible) reset();
  }
);
watch(() => props.resetKey, reset);

const toggleFilters = () => {
  filtersVisible.value = !filtersVisible.value;
  if (!filtersVisible.value) resetFilters();
};

// Drop selected policies when their type is unchecked or when a new list
// arrives (e.g. after switching company).
watch([statuses, () => props.policies], ([value, policies]) => {
  selectedPolicyIds.value = pruneSelectedPolicies(
    selectedPolicyIds.value,
    policies,
    value
  );
});

const policyOptions = computed(() =>
  policiesOfStatuses(props.policies, statuses.value).map((policy) => ({
    label: `${policy.policyNumber} (${policy.status})`,
    value: policy.id
  }))
);

const downloadState = computed(() =>
  getDownloadState({
    filtersVisible: props.filtersEnabled && filtersVisible.value,
    statuses: statuses.value,
    hasDate: Boolean(activeDate.value),
    selectedPolicyIds: selectedPolicyIds.value,
    policies: props.policies
  })
);

// Without filters nothing depends on the policies, so loading only blocks
// the download while the filters are shown.
const waitingForPolicies = computed(
  () => props.loading && props.filtersEnabled && filtersVisible.value
);

const okDisabled = computed(
  () =>
    Boolean(props.blockedReason) ||
    waitingForPolicies.value ||
    !downloadState.value.enabled
);
const okDisabledText = computed(
  () =>
    props.blockedReason ||
    (waitingForPolicies.value ? LOADING_POLICIES_REASON : '') ||
    downloadState.value.reason
);

const close = () => {
  emit('update:visible', false);
};

const submit = () => {
  if (okDisabled.value) return;
  emit(
    'download',
    buildStockDownloadParams({
      filtersVisible: props.filtersEnabled && filtersVisible.value,
      statuses: statuses.value,
      selectedPolicyIds: selectedPolicyIds.value,
      activeDate: activeDate.value
        ? moment(activeDate.value).format('YYYY-MM-DD')
        : null,
      policies: props.policies
    })
  );
};
</script>

<style scoped>
.ok-button-wrapper {
  display: inline-block;
  margin-left: 8px;
}

.info-icon {
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.45);
  cursor: help;
}
</style>
